import './GTable.scss';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { IconButton } from '@mui/joy';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { Fragment, ReactElement, useEffect, useRef, useState } from 'react';
import { S_Pagination, S_PaginationProps } from '../../../design/pagination/S_Pagination';
import { ExpandLessIcon } from '../../../design/static/icons/ExpandLessIcon';
import { ExpandMoreIcon } from '../../../design/static/icons/ExpandMoreIcon';
import { GLOBAL_NONCE } from '../../../ThemeProvider';
import { Loader } from '../Loader';
import { IGRowModel, IGSubRowModel } from './GCell';
import { GHeader } from './GHeader';
import { GRow } from './GRow';
import { IGColumnProps, IGSubColumnProps } from './IGColumnProps';
export { IGColumnProps } from './IGColumnProps';

interface IGTableProps<T extends IGRowModel, D extends IGSubRowModel> {
  className?: string;
  isLoading: boolean;
  columns: IGColumnProps<T>[];
  subColumns?: IGSubColumnProps<D>[];
  items: T[];
  noItemsPlaceholder: string;
  paginationProps?: S_PaginationProps;
  sortingModel?: IGSortingModel;
  enableDrag?: boolean;
  onSortChanged?(orderBy: string, orderType: string): Promise<void> | void;
  onRowDragEnd?(from: number, to: number): Promise<void>;
}

type IGSortingModel = { orderBy: string; orderType: string }[];

export const GTable = observer(<T extends IGRowModel<D>, D extends IGSubRowModel = IGSubRowModel>(props: IGTableProps<T, D>): ReactElement => {
  const [containerWidth, setContainerWidth] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);
  const [expandedRowsIds, setExpandedRowsIds] = useState<string[]>([]);
  const [columns, setColumns] = useState<IGColumnProps<T>[]>(calculateColumns(props));

  const isDragging = useRef<boolean>(false);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  function calculateColumns(props: IGTableProps<T, D>): IGColumnProps<T>[] {
    const columns = [...props.columns];

    if (props.items.some((i) => i.subRows && i.subRows.length !== 0)) {
      columns.push({
        headerName: '',
        width: 'min-content',
        actions: true,
        renderer: (item) => (
          <>
            {item.subRows && item.subRows.length > 0 && (
              <IconButton>{expandedRowsIds.some((i) => i === item.id) ? <ExpandMoreIcon /> : <ExpandLessIcon />}</IconButton>
            )}
          </>
        ),
      });
    }

    return columns;
  }

  useEffect(() => {
    parentRef.current = tableContainerRef.current?.closest('.g-table-container') as HTMLDivElement;

    const trackSize = (): void => {
      setContainerWidth(parentRef.current?.offsetWidth ?? 0);
      setTableWidth(tableContainerRef.current?.offsetWidth ?? 0);
    };
    trackSize();

    resizeObserverRef.current = new ResizeObserver(trackSize);
    resizeObserverRef.current.observe(parentRef.current);
    scrollRef.current?.addEventListener('scroll', handleScrollScroll);
    parentRef.current.addEventListener('scroll', handleTableScroll);

    return (): void => {
      scrollRef.current?.removeEventListener('scroll', handleScrollScroll);
      parentRef.current?.removeEventListener('scroll', handleTableScroll);
      resizeObserverRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    setColumns(calculateColumns(props));
  }, [props.columns]);

  const handleRowClicked = (item: T): void => {
    const expandRow = (id: string): void => {
      setExpandedRowsIds((prev) => [...prev, id]);
    };

    const collapseRow = (id: string): void => {
      setExpandedRowsIds((prev) => prev.filter((i) => i !== id));
    };

    if (!item.subRows || item.subRows.length === 0) {
      return;
    }

    if (expandedRowsIds.every((i) => i !== item.id)) {
      expandRow(item.id);
    } else {
      collapseRow(item.id);
    }
  };

  const handleScrollScroll = (event: Event): void => {
    if (parentRef.current) {
      parentRef.current.scrollLeft = (event.target as HTMLDivElement).scrollLeft;
    }
  };

  const handleTableScroll = (): void => {
    if (parentRef.current) {
      parentRef.current.scrollLeft = scrollRef.current?.scrollLeft ?? 0;
    }
  };

  const handleDragStart = (): void => {
    isDragging.current = true;
  };

  const handleDragEnd = async (result: DropResult): Promise<void> => {
    isDragging.current = false;

    if (result.destination && props.onRowDragEnd) {
      let sourceIndex = result.source.index;
      let destinationIndex = result.destination.index;

      if (props.paginationProps) {
        const { pageNumber, pageSize } = props.paginationProps;
        const pageOffset = (pageNumber - 1) * pageSize;
        sourceIndex = pageOffset + sourceIndex;
        destinationIndex = pageOffset + destinationIndex;
      }

      await props.onRowDragEnd(sourceIndex, destinationIndex);
    }
  };

  const renderHeaders = (): ReactElement => (
    <>
      {!props.isLoading &&
        columns.map((column: IGColumnProps<T>): ReactElement => {
          const sortBy = column.sortBy?.toString();
          let orderType: string | undefined = undefined;
          let onSortChanged: ((orderType: string) => Promise<void>) | undefined = undefined;

          if (sortBy) {
            orderType = props.sortingModel?.find((sm) => sm.orderBy.toLocaleLowerCase() === sortBy.toLocaleLowerCase())?.orderType.toLocaleLowerCase();

            onSortChanged = async (orderType: string): Promise<void> => {
              await props.onSortChanged?.(sortBy, orderType);
            };
          }

          const columnWidth = column.width === 'min-content' ? '1px' : column.width;

          return (
            <GHeader
              key={`header-${column.headerName}`}
              name={column.headerName}
              className={column.headerClassName}
              width={columnWidth}
              tooltip={column.headerTooltip}
              onSortChanged={onSortChanged}
              orderType={orderType}
              headerRenderer={column.headerRenderer}
              sticky={column.sticky}
            />
          );
        })}
    </>
  );

  const renderSubRows = (item: T): ReactElement => {
    return (
      <>
        {props.subColumns &&
          item.subRows &&
          item.subRows.map((subItem) => (
            <tr
              className='g-row g-row-sub'
              key={`table-key-sub-item-${subItem.id}`}
            >
              {props.subColumns?.map((c: IGSubColumnProps<D>, index: number): ReactElement => {
                return (
                  <td
                    key={`${subItem.id}-${index}`}
                    className={classNames('g-cell', c.cellClassName)}
                  >
                    <div className='g-cell-content'>{c.renderer(subItem)}</div>
                  </td>
                );
              })}
              <td className='g-cell'>
                <div className='g-cell-content' />
              </td>
            </tr>
          ))}
      </>
    );
  };

  const renderRows = (): ReactElement => {
    const getHandleRowClicked = (item: T) => (): void => {
      handleRowClicked(item);
    };

    return (
      <>
        {props.items.map((item: T, index: number) => (
          <Fragment key={item.id}>
            <GRow
              onClick={getHandleRowClicked(item)}
              columns={columns}
              enableDrag={props.enableDrag}
              index={index}
              item={item}
              isDragging={isDragging.current}
            />
            {expandedRowsIds.some((i) => i === item.id) && renderSubRows(item)}
          </Fragment>
        ))}
      </>
    );
  };

  const renderNoItemsPlaceholder = (): ReactElement => {
    return <>{props.items.length === 0 && props.noItemsPlaceholder && <div className='g-empty'>{props.noItemsPlaceholder}</div>}</>;
  };

  const renderPagination = (): ReactElement => {
    return (
      <div className='g-table-pagination'>
        {props.paginationProps && <S_Pagination {...props.paginationProps} />}
        <div
          className='g-vertical-scroll-container'
          css={{ width: containerWidth }}
          ref={scrollRef}
        >
          <div
            className='g-vertical-scroll'
            css={{ width: tableWidth }}
          >
            {'The Elder Scroll'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Loader
      isLoading={props.isLoading}
      message={'Please wait, fetching the latest data...'}
    >
      <div className={classNames('table-container', props.className)}>
        <DragDropContext
          nonce={GLOBAL_NONCE}
          onBeforeDragStart={handleDragStart}
          onDragEnd={(res) => {
            void handleDragEnd(res);
          }}
        >
          <div className='g-table-container'>
            <Droppable droppableId='g-droppable'>
              {(provided): ReactElement => (
                <table
                  ref={(ref): void => {
                    provided.innerRef(ref);
                    tableContainerRef.current = ref;
                  }}
                  className='g-table'
                  {...provided.droppableProps}
                >
                  <tbody>
                    <tr className='g-headers'>{renderHeaders()}</tr>
                    {!props.isLoading && renderRows()}
                    {provided.placeholder}
                  </tbody>
                </table>
              )}
            </Droppable>
            {!props.isLoading && renderNoItemsPlaceholder()}
          </div>
          {renderPagination()}
        </DragDropContext>
      </div>
    </Loader>
  );
});
